/**
 * Template Interpolation Utilities
 *
 * Provides utilities for resolving template variables in prompts and inputs.
 * Supports {{stepName.output}} syntax with nested property access.
 *
 * @packageDocumentation
 */

import { TemplateError } from '../types/errors';

/**
 * Resolves template variables in a string.
 *
 * Supports syntax:
 * - {{stepName.output}} - Access entire output
 * - {{stepName.output.field}} - Access nested field
 * - {{stepName.output.nested.path}} - Deep property access
 *
 * @param template - Template string with {{variable}} placeholders
 * @param context - Map of step names to their outputs
 * @returns Resolved string with variables replaced
 * @throws {TemplateError} If referenced step doesn't exist or property path is invalid
 *
 * @example
 * ```typescript
 * const context = new Map([
 *   ['extract', { invoice: { total: 1500 } }]
 * ]);
 *
 * interpolate('Total: {{extract.output.invoice.total}}', context);
 * // Returns: "Total: 1500"
 * ```
 */
export function interpolate(template: string, context: Map<string, any>): string {
  if (typeof template !== 'string') {
    return template;
  }

  // Match all {{...}} patterns
  const pattern = /\{\{([^}]+)\}\}/g;
  let result = template;
  const matches = Array.from(template.matchAll(pattern));

  for (const match of matches) {
    const fullMatch = match[0]; // e.g., "{{extract.output}}"
    const expression = match[1].trim(); // e.g., "extract.output"

    try {
      const value = resolveExpression(expression, context);
      const replacement = formatValue(value);
      result = result.replace(fullMatch, replacement);
    } catch (error) {
      if (error instanceof TemplateError) {
        throw error;
      }
      throw new TemplateError(
        `Failed to resolve template variable "${fullMatch}": ${
          error instanceof Error ? error.message : String(error)
        }`,
        fullMatch
      );
    }
  }

  return result;
}

/**
 * Resolves a template expression to its value.
 *
 * @param expression - Expression like "stepName.output.field"
 * @param context - Map of step outputs
 * @returns Resolved value
 * @throws {TemplateError} If step or property doesn't exist
 */
function resolveExpression(expression: string, context: Map<string, any>): any {
  const parts = expression.split('.');

  if (parts.length < 2) {
    throw new TemplateError(
      `Invalid template expression "${expression}". Expected format: "stepName.output[.field]"`,
      `{{${expression}}}`
    );
  }

  const stepName = parts[0];
  const accessor = parts[1];

  // Verify step exists
  if (!context.has(stepName)) {
    throw new TemplateError(
      `Cannot resolve template variable "{{${expression}}}": step "${stepName}" not found or has not executed yet`,
      `{{${expression}}}`
    );
  }

  let value = context.get(stepName);
  let startIndex = 2; // Start navigating from index 2 by default

  // Special case: "input" allows direct property access (e.g., {{input.fieldName}})
  // Regular steps require "output" accessor (e.g., {{stepName.output.fieldName}})
  if (stepName === 'input') {
    // For input, the second part is the property name, not "output"
    startIndex = 1; // Start navigating from index 1 (the accessor becomes the first property)
  } else {
    // For regular steps, enforce "output" as the accessor
    if (accessor !== 'output') {
      throw new TemplateError(
        `Invalid template expression "${expression}". Second part must be "output"`,
        `{{${expression}}}`
      );
    }
  }

  // Navigate nested properties
  for (let i = startIndex; i < parts.length; i++) {
    const prop = parts[i];

    if (value === null || value === undefined) {
      throw new TemplateError(
        `Cannot access property "${prop}" in "{{${expression}}}": parent value is ${value}`,
        `{{${expression}}}`
      );
    }

    if (typeof value !== 'object') {
      throw new TemplateError(
        `Cannot access property "${prop}" in "{{${expression}}}": parent is not an object`,
        `{{${expression}}}`
      );
    }

    if (!(prop in value)) {
      throw new TemplateError(
        `Cannot access property "${prop}" in "{{${expression}}}": property does not exist`,
        `{{${expression}}}`
      );
    }

    value = value[prop];
  }

  return value;
}

/**
 * Formats a value for string interpolation.
 *
 * @param value - Value to format
 * @returns String representation
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Checks if a string contains template variables.
 *
 * @param str - String to check
 * @returns True if string contains {{...}} patterns
 */
export function hasTemplateVariables(str: string): boolean {
  if (typeof str !== 'string') {
    return false;
  }
  return /\{\{[^}]+\}\}/.test(str);
}

/**
 * Recursively interpolates template variables in any value.
 *
 * @param value - Value to interpolate (can be string, object, array, etc.)
 * @param context - Map of step outputs
 * @returns Interpolated value
 */
export function interpolateValue(value: any, context: Map<string, any>): any {
  if (typeof value === 'string') {
    return interpolate(value, context);
  }

  if (Array.isArray(value)) {
    return value.map((item) => interpolateValue(item, context));
  }

  if (value !== null && typeof value === 'object') {
    const result: any = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = interpolateValue(val, context);
    }
    return result;
  }

  return value;
}
